import { getServerSideURL } from '@/utilities/getURL'
import { DEFAULT_LOCALE } from './catalog'
import { stripLocalePrefix } from './localePath'
import { buildLocalizedPath } from './localizedPath'

/**
 * Localiza um href de TEXTO LIVRE preenchido no admin (ex.: campo «Link de revisão» do bloco
 * Comparativo). Regra pedida pelo dono (2026-07-22): «se estou em /en e clico num link, ele deve
 * levar para o idioma correto».
 *
 * - Link INTERNO (relativo `/...` OU absoluto do nosso host) → herda o locale ativo:
 *   `/seguradora-x` em /en vira `/en/seguradora-x`. Esta função SÓ troca o prefixo: o caminho continua o pt, e a
 *   rota responde 308 para o caminho traduzido quando ele existe. B2 (links internos sem 308): no RichText, no
 *   ComparisonListing e nos ancestrais da trilha, o resultado daqui passa em seguida pelo tradutor de href
 *   (`rotasTraduzidas`), que o troca pela URL final 200 dessa cadeia ou o deixa como está — ver
 *   `docs/links-internos/LINKS_INTERNOS_PAGINAS_TRADUZIDAS_PLANO_2026_09_14.md`. As demais superfícies seguem com o 308.
 *   Qualquer prefixo de locale já colado pelo editor (`/es/...`) é removido antes de re-aplicar.
 * - Link EXTERNO (outro host — o CTA de afiliado do seguradora) → **intocado**.
 * - Vazio / `#` → devolvido como está.
 *
 * Mesma filosofia do RichText (links do corpo herdam o locale — buildLocalizedPath), agora para
 * campos de URL avulsos. Puro; recebe o locale (não lê a request).
 */
export function localizeInternalHref(
  href: string | null | undefined,
  locale: string | undefined,
): string | null {
  const raw = (href ?? '').trim()
  if (!raw || raw === '#') return raw || null

  let path: string | null = null
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    path = raw // relativo interno (`//host/x` é protocol-relative EXTERNO → cai no else)
  } else {
    try {
      const u = new URL(raw)
      const site = new URL(getServerSideURL())
      // Mesmo-host aceita a variante apex⇄www (ex.: www.balizoassessoria.com.br conta como interno).
      // Comparação EXATA contra o conjunto — nada de sufixo/substring, que abriria falso-interno para
      // domínios de terceiros (ex.: balizoassessoria.com.br.evil.com). Preview/staging (efêmeros) ficam de fora.
      const bareHost = site.hostname.startsWith('www.') ? site.hostname.slice(4) : site.hostname
      const hostAllow = new Set([site.hostname, bareHost, `www.${bareHost}`])
      if (hostAllow.has(u.hostname)) path = `${u.pathname}${u.search}${u.hash}`
    } catch {
      /* não é URL absoluta válida → trata como externo (não mexe) */
    }
  }
  if (path === null) return raw // externo → intocado

  // Remove prefixo de locale já presente. `stripLocalePrefix` cobre os NÃO-padrão (/en,/es,…);
  // um `/pt` (padrão) colado à mão não é prefixo de URL válido no site, mas o editor pode digitar —
  // então também o descartamos para não gerar `/en/pt/...`.
  const stripped = stripLocalePrefix(path)
  let cleanPath = stripped ? stripped.rest : path
  const firstSegEnd = cleanPath.indexOf('/', 1)
  const firstSeg = (firstSegEnd === -1 ? cleanPath.slice(1) : cleanPath.slice(1, firstSegEnd)).toLowerCase()
  if (firstSeg === DEFAULT_LOCALE) cleanPath = firstSegEnd === -1 ? '/' : cleanPath.slice(firstSegEnd) || '/'

  return buildLocalizedPath(cleanPath, locale ?? DEFAULT_LOCALE, DEFAULT_LOCALE)
}
